import { Divider, Form } from 'antd';

import { PageProps } from '@/types/PageProps';

import { DefaultRouteTabs } from '@/components/_common';
import ButtonsToGenerateReports from '@/components/Reports/ButtonsToGenerateReports';
import PurchaseReportForm from '@/components/Reports/PurchasesReports/PurchasesReportForm';
import {
  GroupReportByPurchases,
  PurchaseDataSynthetic,
  PurchaseReportRequest,
  PurchasesReportFormValues,
} from '@/core/domain/reports/purchases/purchases/PurchasesReport.types';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import { useReportGenerate } from '@/hooks';
import useCommonGenerateReport from '@/core/domain/reports/commons/Report.hook';
import useFileLoading, { FILE_FORMAT, FileType } from '@/hooks/useFileLoading';
import { ReportCallback } from '@/hooks/useReportGenerate';
import moment from 'moment';
import {
  usePurchaseCfopData,
  usePurchaseDateSupplierWorkorder,
  usePurchaseDocumentType,
  usePurchaseSupplierSynthetic,
} from '@/core/domain/reports/purchases/purchases/PurchaseReport.hooks';
import generateCsv from '@/utils/functions/generateCsv';
import { quantityFormatter } from '@/utils/number';

function PurchasesReportsPage({ route }: PageProps) {
  const intl = useIntl();
  const [form] = Form.useForm<PurchasesReportFormValues>();
  const report = useReportGenerate();
  const reportType = Form.useWatch('reportType', form);
  const watchSynthetic = Form.useWatch('synthetic', form);
  const { setFileLoading, setFileUnloading, fileLoading } = useFileLoading();

  const { mutateAsync: purchaseCfopData } = usePurchaseCfopData();
  const { mutateAsync: purchaseDateSupplierWorkorder } =
    usePurchaseDateSupplierWorkorder();
  const { mutateAsync: purchaseDocumentType } = usePurchaseDocumentType();
  const { mutateAsync: purchaseSupplierSynthetic } =
    usePurchaseSupplierSynthetic();

  const generateReport = useCommonGenerateReport(
    reportType === GroupReportByPurchases.cfop
      ? '/generate/purchase-cfop'
      : reportType === GroupReportByPurchases.documentType
      ? '/generate/purchase-document-type'
      : watchSynthetic
      ? '/generate/purchase-supplier-synthetic'
      : '/generate/purchase-date-supplier-workorder',
    'Relatório gerado com sucesso!',
    'Relatório de compra gerado com sucesso.',
  );

  const handleGenerateReport = async (
    format: FileType,

    callback?: ReportCallback,
  ) => {
    setFileLoading(format);

    try {
      const {
        initialDate,
        finalDate,
        dateType,
        lsPurchaseItems,
        lsSituacao,
        reportType,
        synthetic,
        companiesIds,
        cfopIds,
        documentTypeIds,
        suppliersIds,
        workOrderComplementIds,
        purchaseCode,
        invoiceKey,
        documentNumber,
      } = await form.validateFields();

      const obj: PurchasesReportFormValues & { format: string } = {
        invoiceKey,
        purchaseCode,
        cfopIds,
        documentTypeIds,
        suppliersIds,
        workOrderComplementIds,
        companiesIds: companiesIds,
        initialDate: moment(initialDate).format('YYYY-MM-DD'),
        finalDate: moment(finalDate).format('YYYY-MM-DD'),
        dateType,
        lsPurchaseItems,
        lsSituacao,
        documentNumber,
        reportType: synthetic
          ? GroupReportByPurchases.supplierSynthetic
          : reportType,
        format: FILE_FORMAT[format],
      };

      const result = await generateReport.mutateAsync(obj);

      if (callback) {
        callback(result);
      }
    } catch {}

    setFileUnloading(format);
  };

  const handleView = () => {
    handleGenerateReport('REPORT_VIEW', (result) =>
      report.openFile({
        result,
        fileName: 'relatorio_contas_a_pagar.pdf',
        fileType: 'application/pdf',
      }),
    );
  };

  const handlePdf = () => {
    handleGenerateReport('PDF', (result) =>
      report.downloadFile({
        result,
        fileName: 'relatorio_contas_a_pagar.pdf',
      }),
    );
  };

  const handleCsv = async () => {
    const values = form.getFieldsValue();
    const obj: PurchaseReportRequest = {
      ...values,
      initialDate: moment(values.initialDate).format('YYYY-MM-DD'),
      finalDate: moment(values.finalDate).format('YYYY-MM-DD'),
    };

    const syntheticData = (data: PurchaseDataSynthetic) => {
      return {
        ['Valor dos itens']: quantityFormatter(data.valueLiquidItem),
        ['Valor do Frete']: quantityFormatter(data.totalDeliveryValue),
        ['Valor Despesas']: quantityFormatter(data.totalExpensesValue),
        ['Valor Ipi']: quantityFormatter(data.totalIpi),
        ['Valor ICMS ST']: quantityFormatter(data.totalIcmsSt),
        ['Valor Devolvido']: quantityFormatter(data.totalLiquidReturned),
        ['Valor Total']: quantityFormatter(
          data.valueLiquidItem +
            data.totalDeliveryValue +
            data.totalExpensesValue +
            data.totalIpi +
            data.totalIcmsSt -
            data.totalLiquidReturned,
        ),
      };
    };

    const syntheticTotal = (data: PurchaseDataSynthetic[]) => {
      return [
        [
          'Total',
          ...data
            .reduce(
              (prev, cur) => {
                prev[0] += cur.valueLiquidItem;
                prev[1] += cur.totalDeliveryValue;
                prev[2] += cur.totalExpensesValue;
                prev[3] += cur.totalIpi;
                prev[4] += cur.totalIcmsSt;
                prev[5] += cur.totalLiquidReturned;
                prev[6] +=
                  cur.valueLiquidItem +
                  cur.totalDeliveryValue +
                  cur.totalIpi +
                  cur.totalIcmsSt +
                  cur.totalExpensesValue -
                  cur.totalLiquidReturned;
                return prev;
              },
              [0, 0, 0, 0, 0, 0, 0],
            )
            .map(quantityFormatter),
        ],
      ];
    };

    const csvHeader = (title: string) => [
      [title, moment().format('DD/MM/YYYY')],
      ['De', obj.initialDate, 'Até', obj.finalDate],
      [],
    ];

    try {
      setFileLoading('CSV');
      if (reportType === GroupReportByPurchases.cfop) {
        const data = await purchaseCfopData(obj);
        const csvData = data.map((cfop) => ({
          ['CFOP']: cfop.codeCfop + ' - ' + cfop.descriptionCfop,
          ...syntheticData(cfop),
        }));

        generateCsv(
          csvData,
          'compras por cfop',
          csvHeader('Relatório de compra por CFOP'),
          syntheticTotal(data),
        );
      } else if (reportType === GroupReportByPurchases.documentType) {
        const data = await purchaseDocumentType(obj);
        const csvData = data.map((docType) => ({
          ['Tipo de Documento']:
            docType.typeDocument + ' - ' + docType.descriptionDocument,
          ...syntheticData(docType),
        }));
        generateCsv(
          csvData,
          'compras por tipo de documento',
          csvHeader('Relatório de compra por tipo de documento'),
          syntheticTotal(data),
        );
      } else if (
        reportType === GroupReportByPurchases.supplier &&
        !!watchSynthetic
      ) {
        const data = await purchaseSupplierSynthetic({
          ...obj,
          reportType: GroupReportByPurchases.supplierSynthetic,
        });
        const csvData = data.map((supplier) => ({
          ['Fornecedor']: supplier.nameSupplier,
          ...syntheticData(supplier),
        }));
        generateCsv(
          csvData,
          'compra por fornecedor',
          csvHeader('Relatório de compra por fornecedor'),
          syntheticTotal(data),
        );
      } else {
        const data = await purchaseDateSupplierWorkorder(obj);
        const csvData = data.map((cur) => {
          const validateItem = (value = '', typeI = '', typeP = '') =>
            cur.typeItem === 'I'
              ? typeI
              : cur.workOrderItemId <= 0 &&
                cur.typeItem === 'P' &&
                cur?.quantityReturned === 0
              ? typeP
              : value;

          return {
            ['OS']: cur.workOrderItemId
              ? cur.numberWorkOrder + '/' + cur.numberComplementWorkOrder
              : '',
            ['Veículo']: cur.licensePlate
              ? cur.licensePlate + ' - ' + cur.nameBrand + ' - ' + cur.nameModel
              : '',
            ['Código']: cur.codePurchase,
            ['Situação']: cur.situation,
            ['Nota Fiscal']: cur.documentNumber,
            ['Data lançamento']: moment(cur.releaseDate).format('DD/MM/YYYY'),
            ['Data emissão']: moment(cur.issueDate).format('DD/MM/YYYY'),
            ['Fornecedor']: cur.nameSupplier,
            ['Código item']: cur.manufacturerCode,
            ['Item da compra']: cur.nameItem,
            ['Quantidade']: quantityFormatter(cur.quantityPurchase),
            ['Valor Bruto']: quantityFormatter(cur.unitaryValuePurchase),
            ['Desconto %']: quantityFormatter(cur.discountValuePurchase),
            ['Valor líquido']: quantityFormatter(cur.valueLiquidItemPurchase),
            ['CFOP']: cur.codeCfop,
            ['Devolução']: quantityFormatter(cur.valueLiquidReturned),
            ['Venda Bruta']: validateItem(
              quantityFormatter(
                cur.quantityPurchase * cur.unitaryComplementItem,
              ),
              'Insumo',
              'Estoque de peças',
            ),
            ['Desc. %']: validateItem(
              quantityFormatter(cur.discountPercentComplementItem * 100),
            ),

            ['Venda líquida']: validateItem(
              quantityFormatter(
                cur?.quantityPurchase ||
                  0 * cur?.unitaryComplementItem ||
                  0 -
                    cur.quantityPurchase *
                      cur.unitaryComplementItem *
                      cur.discountPercentComplementItem,
              ),
            ),
            ['Diferença Bruta']: validateItem(
              quantityFormatter(
                cur.quantityPurchase * cur.unitaryComplementItem -
                  cur.quantityPurchase * cur.unitaryValuePurchase,
              ),
            ),
            ['Diferença líquida']: validateItem(
              quantityFormatter(
                cur.quantityPurchase * cur.unitaryComplementItem -
                  cur.quantityPurchase *
                    cur.unitaryComplementItem *
                    cur.discountPercentComplementItem -
                  cur.valueLiquidItemPurchase,
              ),
            ),
          };
        });
        generateCsv(csvData, 'relatório de compra');
      }
    } catch {
    } finally {
      setFileUnloading('CSV');
    }
  };

  return (
    <DefaultRouteTabs route={route.parent} permissions={route.permissions}>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <PurchaseReportForm form={form} />

      <Divider />

      <ButtonsToGenerateReports
        viewReport={handleView}
        generatePdf={handlePdf}
        generateCsv={handleCsv}
        loading={fileLoading}
      />
    </DefaultRouteTabs>
  );
}

export default PurchasesReportsPage;
